import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { delay } from 'rxjs/operators';

import { ApplicationConfigService } from 'src/app/core/config/application-config.service';
import { CommonService } from 'src/app/shared/services/common.service';
import { EkycRequestService } from 'src/app/admin/assisted-ekyc/shared/ekyc-request.service';
import {
  EkycListType,
  EkycRequestStatus,
  nextStatusFor,
  ScreeningStatus,
} from 'src/app/admin/assisted-ekyc/shared/ekyc-request.enum';
import {
  IEkycRequestDetail,
  IEkycRequestFilter,
  IRiskOverridePayload,
  IStatusUpdatePayload,
} from 'src/app/admin/assisted-ekyc/shared/ekyc-request.model';

import { MockDb } from '../mock-db';
import { buildEkycRequestSeed } from '../fixtures/ekyc-requests.fixture';

const MOCK_LATENCY_MS = 300;
const COLLECTION = 'ekycRequests';

function readAll(): IEkycRequestDetail[] {
  const stored = MockDb.get<IEkycRequestDetail[]>(COLLECTION);
  if (!stored) {
    const seed = buildEkycRequestSeed();
    MockDb.set(COLLECTION, seed);
    return seed;
  }
  return stored;
}

function writeAll(rows: IEkycRequestDetail[]): void {
  MockDb.set(COLLECTION, rows);
}

function contains(value: unknown, filter: unknown): boolean {
  if (filter === null || filter === undefined || filter === '') return true;
  return String(value ?? '').toLowerCase().includes(String(filter).toLowerCase());
}

function equals(value: unknown, filter: unknown): boolean {
  if (filter === null || filter === undefined || filter === '') return true;
  return String(value ?? '') === String(filter);
}

function envelope<T>(data: T) {
  return { responseCode: 'S100000', responseMessage: 'Operation successful', data };
}

function respond<T>(value: T): Observable<T> {
  return of(value).pipe(delay(MOCK_LATENCY_MS));
}

/**
 * Mock Assisted eKYC backend.
 *
 * State is persisted through MockDb, which is the whole reason the store is localStorage-backed:
 * a request submitted by test-maker is still sitting in BRANCH_REVIEW after logging out and back
 * in as test-checker, and moves on to COGB_REVIEW once approved. Without that the demo scripts in
 * contracts-2 Appendix D would not hold together.
 */
@Injectable()
export class MockEkycRequestService extends EkycRequestService {
  constructor(http: HttpClient, commonService: CommonService, appConfig: ApplicationConfigService) {
    super(http, commonService, appConfig);
  }

  override fetchRequestList(listType: EkycListType, query: IEkycRequestFilter): Observable<any> {
    const currentUser = MockDb.getCurrentUser();
    let rows = readAll();

    // Queue scoping — contracts-1 §7.1.
    if (listType === 'maker') {
      rows = rows.filter((r) => r.makerUserId === currentUser?.userIdentity);
    } else if (listType === 'checker') {
      rows = rows.filter(
        (r) => r.status === EkycRequestStatus.BRANCH_REVIEW || r.branchCheckerUserId === currentUser?.userIdentity,
      );
    } else if (listType === 'cogb') {
      rows = rows.filter(
        (r) => r.status === EkycRequestStatus.COGB_REVIEW || r.cogbCheckerUserId === currentUser?.userIdentity,
      );
    }

    rows = rows
      .filter((r) => contains(r.requestId, query.requestId))
      .filter((r) => contains(r.applicantName, query.applicantName))
      .filter((r) => contains(r.smartNid, query.nid) || contains(r.oldNid, query.nid))
      .filter((r) => contains(r.cifNumber, query.cifNumber))
      .filter((r) => contains(r.email, query.email))
      .filter((r) => contains(r.phoneNumber, query.phoneNumber))
      .filter((r) => equals(r.accountClass, query.accountClass))
      .filter((r) => equals(r.ekycType, query.ekycType))
      .filter((r) => contains(r.productTypeName, query.productTypeName))
      .filter((r) => equals(r.branchCode, query.branchCode))
      .filter((r) => equals(r.status, query.requestStatus))
      .filter((r) => equals(r.riskBand, query.riskBand))
      .filter((r) => equals(r.screeningStatus, query.screeningStatus))
      .filter((r) => equals(r.requestKind, query.requestKind))
      .filter((r) => contains(r.selectedAccount, query.accountNumber) || contains(r.createdAccount, query.accountNumber))
      .filter((r) => (query.fromDate ? r.requestedTime >= query.fromDate : true))
      .filter((r) => (query.toDate ? r.requestedTime <= `${query.toDate}T23:59:59.999Z` : true))
      .sort((a, b) => b.requestedTime.localeCompare(a.requestedTime));

    const pageSize = Number(query.pageSize) || 10;
    const pageNumber = Number(query.pageNumber) || 0;
    const start = pageNumber * pageSize;

    return respond(
      envelope({
        data: rows.slice(start, start + pageSize),
        currentPage: pageNumber,
        pageSize,
        totalItems: rows.length,
      }),
    );
  }

  override requestDetails(requestId: string): Observable<any> {
    return respond(envelope(readAll().find((r) => r.requestId === requestId) ?? null));
  }

  override updateStatus(listType: EkycListType, payload: IStatusUpdatePayload): Observable<any> {
    const tier = listType === 'cogb' ? 'cogb' : 'checker';
    const currentUser = MockDb.getCurrentUser();
    const rows = readAll();
    const index = rows.findIndex((r) => r.requestId === payload.requestId);

    if (index < 0) {
      return respond({ responseCode: 'E404', responseMessage: 'Request not found', data: null });
    }

    const request = rows[index];

    // Server-side segregation of duties. The UI hides the buttons, but the rule is enforced here
    // too — hiding a control is presentation, not a control.
    if (request.makerUserId === currentUser?.userIdentity) {
      return respond({ responseCode: 'E403', responseMessage: 'You created this application and cannot check it.', data: null });
    }
    if (tier === 'cogb' && request.branchCheckerUserId === currentUser?.userIdentity) {
      return respond({
        responseCode: 'E403',
        responseMessage: 'You performed the branch review and cannot also give final approval.',
        data: null,
      });
    }
    if (request.status === EkycRequestStatus.SCREENING_HOLD) {
      return respond({ responseCode: 'E403', responseMessage: 'Application is on Screening Hold.', data: null });
    }

    const next = nextStatusFor(request.status, payload.action, tier);
    if (!next) {
      return respond({ responseCode: 'E400', responseMessage: 'That action is not valid in the current state.', data: null });
    }

    const now = new Date().toISOString();
    const remark = { remarks: payload.remarks, userName: currentUser?.fullName ?? 'Unknown', at: now };

    const updated: IEkycRequestDetail = {
      ...request,
      status: next,
      ...(tier === 'checker'
        ? { branchCheckerUserId: currentUser?.userIdentity ?? null, checkerRemarks: [...request.checkerRemarks, remark] }
        : { cogbCheckerUserId: currentUser?.userIdentity ?? null, cogbRemarks: [...request.cogbRemarks, remark] }),
      returnCount: payload.action === 'RETURN' ? request.returnCount + 1 : request.returnCount,
      additionalDocuments: payload.additionalDocuments ?? request.additionalDocuments,
      stateHistory: [
        ...request.stateHistory,
        {
          from: request.status,
          to: next,
          actor: currentUser?.fullName ?? 'Unknown',
          role: tier === 'checker' ? 'BRANCH_CHECKER' : 'COGB_CHECKER',
          at: now,
          remarks: payload.remarks,
        },
      ],
    };

    // Auto-advance: branch approval moves straight into the COGB queue; COGB approval starts CBS.
    const advanced = this.autoAdvance(updated, currentUser?.fullName ?? 'System');

    rows[index] = advanced;
    writeAll(rows);
    return respond(envelope(advanced));
  }

  /**
   * Mirrors the "→ auto →" transitions in contracts-2 Appendix B. Kept here rather than in the
   * component so the queues stay consistent no matter who triggered the change.
   */
  private autoAdvance(request: IEkycRequestDetail, actor: string): IEkycRequestDetail {
    const now = new Date().toISOString();

    if (request.status === EkycRequestStatus.BRANCH_APPROVED) {
      return {
        ...request,
        status: EkycRequestStatus.COGB_REVIEW,
        stateHistory: [
          ...request.stateHistory,
          { from: EkycRequestStatus.BRANCH_APPROVED, to: EkycRequestStatus.COGB_REVIEW, actor, role: 'SYSTEM', at: now, remarks: 'Document pack forwarded to COGB queue.' },
        ],
      };
    }

    if (request.status === EkycRequestStatus.COGB_APPROVED) {
      // CBS is mocked: it succeeds unless the applicant name carries the FAIL marker.
      const cbsFails = request.applicantName.toUpperCase().includes('FAIL');
      const accountNumber = `01013300${String(20000 + Math.floor(Math.random() * 9999))}`;

      if (cbsFails) {
        return {
          ...request,
          status: EkycRequestStatus.CBS_FAILED,
          cbsAttemptCount: 3,
          cbsLastFailureReason: 'CBS returned 504 on CIF create after 3 idempotent retries',
          stateHistory: [
            ...request.stateHistory,
            { from: EkycRequestStatus.COGB_APPROVED, to: EkycRequestStatus.CBS_FAILED, actor: 'System', role: 'SYSTEM', at: now, remarks: 'CIF creation failed.' },
          ],
        };
      }

      return {
        ...request,
        status: EkycRequestStatus.COMPLETED,
        createdAccount: accountNumber,
        cifNumber: request.cifNumber ?? `CIF${Math.floor(100000 + Math.random() * 899999)}`,
        stateHistory: [
          ...request.stateHistory,
          { from: EkycRequestStatus.COGB_APPROVED, to: EkycRequestStatus.CBS_PENDING, actor: 'System', role: 'SYSTEM', at: now, remarks: null },
          { from: EkycRequestStatus.CBS_PENDING, to: EkycRequestStatus.ACCOUNT_CREATED, actor: 'System', role: 'SYSTEM', at: now, remarks: `Account ${accountNumber} created.` },
          { from: EkycRequestStatus.ACCOUNT_CREATED, to: EkycRequestStatus.COMPLETED, actor: 'System', role: 'SYSTEM', at: now, remarks: 'Service enrollment dispatched.' },
        ],
      };
    }

    return request;
  }

  /**
   * Turns the wizard form into a queued request.
   *
   * Screening decides the landing state: a potential match goes to SCREENING_HOLD rather than
   * BRANCH_REVIEW, and is frozen there (SRS §M6). The risk model version is pinned here and never
   * recomputed — a later configuration change must not re-score this application (SRS §M7).
   */
  override submitRequest(payload: any): Observable<any> {
    const form = payload?.form ?? {};
    const screening = payload?.screening ?? {};
    const grading = payload?.riskGrading ?? {};
    const currentUser = MockDb.getCurrentUser();
    const rows = readAll();
    const now = new Date().toISOString();

    const sequence = rows.length + 1;
    const requestId = `AEK-2026-${String(sequence).padStart(4, '0')}`;
    const ec = form.ecVerification ?? {};
    const requestKind = form.requestKind === 'UPGRADE' ? 'UPGRADE' : 'NEW';
    const held = screening.status === ScreeningStatus.POTENTIAL_MATCH;
    const status = held ? EkycRequestStatus.SCREENING_HOLD : EkycRequestStatus.BRANCH_REVIEW;

    const services = this.buildServiceRequests(requestId, form.serviceEnrollment ?? {});

    const created: IEkycRequestDetail = {
      requestId,
      applicantName: ec.applicantNameEnglish ?? 'Unknown',
      dateOfBirth: ec.dateOfBirth ?? '',
      oldNid: null,
      smartNid: ec.nidNumber ?? '',
      // Captured on Customer Information; the IB notification fields are only a fallback now.
      email: form.customerInfo?.emailAddress ?? form.serviceEnrollment?.ibNotificationEmail ?? '',
      phoneNumber: form.customerInfo?.mobileNumber ?? form.serviceEnrollment?.ibNotificationMobile ?? '',
      cifNumber: null,
      accountClass: form.selectedAccountClass ?? 'CONVENTIONAL',
      productTypeName: form.accountInfo?.productTypeName ?? '',
      branchCode: form.accountInfo?.branchCode ?? '',
      branchName: form.accountInfo?.branchName ?? '',
      selectedAccount: form.selectedAccount?.accountNo ?? form.sourceAccountNo ?? null,
      createdAccount: null,
      ekycType: form.ekycType ?? 'REGULAR',
      requestKind,
      upgradeStatus: requestKind === 'UPGRADE' ? 'PENDING_APPROVAL' : 'NONE',
      sourceRequestId: form.sourceRequestId ?? null,
      sourceAccountNo: form.sourceAccountNo ?? null,
      riskScore: grading.totalScore ?? 0,
      riskBand: grading.band ?? 'LOW',
      screeningStatus: screening.status ?? ScreeningStatus.CLEAR,
      requestedTime: now,
      status,
      makerUserId: currentUser?.userIdentity ?? 'UNKNOWN',
      makerName: currentUser?.fullName ?? 'Unknown',
      branchCheckerUserId: null,
      cogbCheckerUserId: null,
      returnCount: 0,

      selectedRegion: form.selectedRegion ?? 'BANGLADESH',
      consentAcceptedAt: form.consentAcceptedAt ?? now,
      consentVersion: 'TC-2026.1',

      identityType: ec.identityType ?? 'NID',
      ekycDetailsResponse: {
        userIdentity: ec.nidNumber ?? '',
        nationalIdNumber: ec.nidNumber ?? '',
        oldNid: null,
        dateOfBirth: ec.dateOfBirth ?? '',
        gender: form.customerInfo?.gender ?? '',
        fullNameEnglish: ec.applicantNameEnglish ?? '',
        fullNameLocal: ec.applicantNameBangla ?? '',
        fathersNameEnglish: ec.fatherNameEnglish ?? '',
        fathersNameLocal: ec.fatherNameBangla ?? '',
        mothersNameEnglish: ec.motherNameEnglish ?? '',
        mothersNameLocal: ec.motherNameBangla ?? '',
        spouseNameEnglish: form.customerInfo?.spouseName ?? null,
        spouseNameLocal: null,
        incomeSource: form.customerInfo?.incomeSourceCode ?? null,
        monthlyIncome: form.customerInfo?.monthlyIncome ?? null,
        occupation: form.customerInfo?.profession ?? null,
        bloodGroup: null,
        postalCode: ec.permanentAddressGroup?.postCode ?? null,
        postOffice: ec.permanentAddressGroup?.postOffice ?? null,
        presentAddressEnglish: form.customerInfo?.presentAddress ?? '',
        presentAddressLocal: '',
        permanentAddressEnglish: form.customerInfo?.permanentAddress ?? '',
        permanentAddressLocal: '',
      },
      comparisonData: [
        { fieldName: 'Customer Name', ekycValue: ec.applicantNameEnglish ?? '', cbsValue: ec.applicantNameEnglish ?? '', matched: 100 },
        { fieldName: "Father's Name", ekycValue: ec.fatherNameEnglish ?? '', cbsValue: ec.fatherNameEnglish ?? '', matched: 100 },
        { fieldName: "Mother's Name", ekycValue: ec.motherNameEnglish ?? '', cbsValue: ec.motherNameEnglish ?? '', matched: 100 },
      ],
      faceMatch: {
        selfieUrl: ec.selfieImage ?? 'assets/mock/portrait-placeholder.svg',
        nidPhotoUrl: ec.frontImage ?? 'assets/mock/nid-placeholder.svg',
        score: ec.faceMatchScore ?? 0,
        threshold: ec.faceMatchThreshold ?? 75,
        livenessPassed: !!ec.livenessPassed,
        manualReviewRequired: !!ec.manualReviewRequired,
        capturedAt: ec.faceMatchCapturedAt ?? now,
      },
      screening: {
        status: screening.status ?? ScreeningStatus.CLEAR,
        screenedAt: screening.screenedAt ?? now,
        reference: screening.reference ?? null,
        holdReason: screening.holdReason ?? null,
        pepConfirmed: !!screening.pepConfirmed,
      },

      passportNumber: form.customerInfo?.passportNumber ?? null,
      permanentAddress: form.customerInfo?.permanentAddress ?? '',
      presentAddress: form.customerInfo?.presentAddress ?? '',
      gender: form.customerInfo?.gender ?? '',
      maritalStatus: form.customerInfo?.maritalStatus ?? '',
      spouseName: form.customerInfo?.spouseName ?? null,
      religion: form.customerInfo?.religionCode ?? '',
      profession: form.customerInfo?.profession ?? '',
      occupationCategoryName: form.customerInfo?.occupationCategoryName ?? form.customerInfo?.occupationCategoryCode ?? null,
      sourceOfIncome: form.customerInfo?.incomeSourceCode ?? null,
      sectorName: form.customerInfo?.sectorCode ?? null,
      monthlyIncomeInBdt: Number(form.customerInfo?.monthlyIncome ?? 0),
      tinNumber: form.additionalInfo?.tinNumber ?? null,

      accountInformation: {
        productTypeCode: form.accountInfo?.productTypeCode ?? '',
        productTypeName: form.accountInfo?.productTypeName ?? '',
        monthlyTransactionAmount: form.ekycType === 'SIMPLIFIED' ? 'Up to BDT 100,000' : 'More than BDT 100,000',
        isSelectedTransactionLimitExtra: form.ekycType !== 'SIMPLIFIED',
        monthlyTransactionNumber: Number(form.accountInfo?.monthlyTransactionNumber ?? 0),
        divisionName: String(form.accountInfo?.divisionId ?? ''),
        districtName: String(form.accountInfo?.districtId ?? ''),
        thanaOrUpozillaName: String(form.accountInfo?.thanaId ?? ''),
        branchCode: form.accountInfo?.branchCode ?? '',
        branchName: form.accountInfo?.branchName ?? '',
        rmCode: String(form.accountInfo?.rmCode ?? '').trim() || null,
      },

      additionalInfo: {
        items: [
          form.additionalInfo?.tinCertificateUrl && { displayName: 'TIN Certificate', fieldValue: form.additionalInfo.tinCertificateUrl, fieldType: 'file' },
          form.additionalInfo?.taxReceiptUrl && { displayName: 'Tax Acknowledgement Receipt', fieldValue: form.additionalInfo.taxReceiptUrl, fieldType: 'file' },
          form.additionalInfo?.addressProofUrl && { displayName: 'Proof of Address', fieldValue: form.additionalInfo.addressProofUrl, fieldType: 'file' },
          form.additionalInfo?.signatureUrl && { displayName: 'Signature', fieldValue: form.additionalInfo.signatureUrl, fieldType: 'file' },
        ].filter(Boolean) as any[],
      },
      fatcaInformation: form.ekycType === 'SIMPLIFIED' ? null : form.fatcaInfo,
      personalAssessment: form.personalAssessment ?? { isPublicFigure: false, isRelatedToPublicFigure: false },
      nomineeInformation: (form.nominees ?? []).map((nominee: any) => ({
        ...nominee,
        presentAddress: { roadOrVillage: '', district: '', subDistrict: '', postOffice: '', postCode: '' },
        permanentAddress: { roadOrVillage: '', district: '', subDistrict: '', postOffice: '', postCode: '' },
        religion: nominee.religionCode,
      })),
      referrerInformation: form.ekycType === 'SIMPLIFIED' ? null : form.referrerInfo,
      serviceRequests: services,
      transactionProfile: null,
      riskGradingDetails: grading.lines ?? [],
      // Pinned at submission — never recomputed.
      riskModelVersion: grading.modelVersion ?? 'RM-2026.1',
      riskOverride: null,

      documentInformation: [
        ec.frontImage && { type: 'NID_FRONT', path: ec.frontImage, scanStatus: 'CLEAN' as const },
        ec.backImage && { type: 'NID_BACK', path: ec.backImage, scanStatus: 'CLEAN' as const },
        ec.selfieImage && { type: 'CUSTOMER_PHOTO', path: ec.selfieImage, scanStatus: 'CLEAN' as const },
      ].filter(Boolean) as any[],
      additionalDocuments: [],
      branchInformation: {
        accountType: form.accountInfo?.productTypeName ?? '',
        branchName: form.accountInfo?.branchName ?? '',
        branchDistrict: String(form.accountInfo?.districtId ?? ''),
        branchDivision: String(form.accountInfo?.divisionId ?? ''),
      },
      makerRemarks: [{ remarks: 'Captured at counter.', userName: currentUser?.fullName ?? 'Unknown', at: now }],
      checkerRemarks: [],
      cogbRemarks: [],
      stateHistory: [
        { from: null, to: EkycRequestStatus.DRAFT, actor: currentUser?.fullName ?? 'Unknown', role: 'MAKER', at: now, remarks: null },
        { from: EkycRequestStatus.DRAFT, to: EkycRequestStatus.SUBMITTED, actor: currentUser?.fullName ?? 'Unknown', role: 'MAKER', at: now, remarks: null },
        { from: EkycRequestStatus.SUBMITTED, to: status, actor: 'System', role: 'SYSTEM', at: now, remarks: held ? screening.holdReason : 'Screening clear — sent for branch review.' },
      ],
      cbsPrefillFailed: false,
      cbsAttemptCount: 0,
      cbsLastFailureReason: null,
    };

    if (requestKind === 'UPGRADE' && form.sourceRequestId) {
      const sourceIndex = rows.findIndex((row) => row.requestId === form.sourceRequestId);
      if (sourceIndex >= 0) {
        rows[sourceIndex] = { ...rows[sourceIndex], upgradeStatus: 'PENDING_APPROVAL' };
      }
    }

    writeAll([created, ...rows]);
    return respond(envelope(created));
  }

  /** Each service carries its own id and an idempotency key derived from application + type. */
  private buildServiceRequests(requestId: string, enrollment: any) {
    const rows: any[] = [];
    const push = (type: string, extra: Record<string, unknown>) =>
      rows.push({
        serviceRequestId: `${requestId}-SVC-${rows.length + 1}`,
        ekycRequestId: requestId,
        serviceType: type,
        idempotencyKey: `${requestId}:${type}`,
        status: 'PENDING',
        attemptCount: 0,
        attempts: [],
        deliveryBranchCode: null,
        deliveryBranchName: null,
        ...extra,
      });

    if (enrollment.wantsDebitCard) {
      push('DEBIT_CARD', {
        cardProductTypeCode: enrollment.cardProductTypeCode,
        cardProductTypeName: enrollment.cardProductTypeName,
        nameOnCard: enrollment.nameOnCard,
        deliveryBranchCode: enrollment.cardDeliveryBranch,
      });
    }
    if (enrollment.wantsChequeBook) {
      push('CHEQUE_BOOK', {
        chequeBookLeafCount: enrollment.chequeBookLeafCount,
        chequeBookProductType: enrollment.chequeBookProductType,
        deliveryBranchCode: enrollment.chequeBookDeliveryBranch,
      });
    }
    if (enrollment.wantsInternetBanking) {
      push('INTERNET_BANKING', {
        ibConsentAccepted: enrollment.ibConsentAccepted,
        ibNotificationMobile: enrollment.ibNotificationMobile,
        ibNotificationEmail: enrollment.ibNotificationEmail,
      });
    }
    return rows;
  }

  override overrideRisk(payload: IRiskOverridePayload): Observable<any> {
    const currentUser = MockDb.getCurrentUser();
    const rows = readAll();
    const index = rows.findIndex((r) => r.requestId === payload.requestId);
    if (index < 0) {
      return respond({ responseCode: 'E404', responseMessage: 'Request not found', data: null });
    }

    const request = rows[index];
    const now = new Date().toISOString();

    // Both the original and the override are retained permanently — SRS §M7.
    rows[index] = {
      ...request,
      riskBand: payload.overrideBand,
      riskOverride: {
        originalBand: request.riskOverride?.originalBand ?? (request.riskBand ?? 'LOW'),
        originalScore: request.riskOverride?.originalScore ?? (request.riskScore ?? 0),
        overrideBand: payload.overrideBand,
        justification: payload.justification,
        overriddenBy: currentUser?.fullName ?? 'Unknown',
        overriddenAt: now,
      },
      stateHistory: [
        ...request.stateHistory,
        { from: request.status, to: request.status, actor: currentUser?.fullName ?? 'Unknown', role: 'COGB_CHECKER', at: now, remarks: `Risk band overridden to ${payload.overrideBand}: ${payload.justification}` },
      ],
    };

    writeAll(rows);
    return respond(envelope(rows[index]));
  }

  override getBranchList(): Observable<any> {
    return respond(
      envelope([
        { branchId: 'BR-0101', branchName: 'Motijheel Branch' },
        { branchId: 'BR-0102', branchName: 'Gulshan Branch' },
        { branchId: 'BR-0103', branchName: 'Dhanmondi Branch' },
        { branchId: 'BR-0201', branchName: 'Chattogram Agrabad Branch' },
        { branchId: 'HO-0001', branchName: 'Head Office — COGB' },
      ]),
    );
  }

  override export(): Observable<any> {
    const header = 'Request ID,Applicant Name,e-KYC Type,Status,Risk Band,Screening,Branch,Requested\n';
    const body = readAll()
      .map((r) => [r.requestId, r.applicantName, r.ekycType, r.status, r.riskBand, r.screeningStatus, r.branchName, r.requestedTime].join(','))
      .join('\n');
    return respond(new Blob([header + body], { type: 'text/csv;charset=utf-8' }));
  }
}

/** Exposed so chunk 5's service-enrollment queues read the same store. */
export const EkycRequestStore = {
  all: readAll,
  save: writeAll,
  screeningStatuses: ScreeningStatus,
};
